"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, MapPin, Phone, ShieldCheck, Truck, ArrowRight, CircleAlert } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const responseMetrics = [{
  label: "Average Arrival Time",
  value: "30-45 Mins",
  detail: "Direct dispatch from local Jonesboro units"
}, {
  label: "Local Service Coverage",
  value: "100% Clayton Co.",
  detail: "Jonesboro, Riverdale, Morrow & Tara Blvd corridor"
}, {
  label: "Fleet Readiness",
  value: "Fully Stocked",
  detail: "Parts and diagnostic equipment on every truck"
}];
const urgentScenarios = ["Burst pipes & severe indoor water line breaks", "Active flooding & sudden ceiling or slab leaks", "Sewer line backups & overflowing toilets", "Water heater failures, tank ruptures & major leaks"];
export default function LocalService() {
  const prefersReducedMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="local-responsive-service" data-nav="light" className="bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column: Urgent Plumbing Triage & Dispatch Copy */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants} className="lg:col-span-7 flex flex-col items-start">
            {/* Eyebrow Status Pill */}
            <div className="inline-flex items-center gap-2 rounded-lg bg-secondary px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-secondary-foreground mb-4">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-lg h-2 w-2 bg-accent"></span>
              </span>
              <span>24/7 Emergency Plumbing Triage</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15] mb-5">
              Emergency Plumbing Triage & Rapid Dispatch Across Jonesboro
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
              When dealing with an active burst pipe, major water leak, sewer backup, overflowing toilet, water heater leak, or active flooding, immediate intervention stops catastrophic property damage. Call now for instant phone triage with local Georgia technicians—mobile response units are dispatched immediately with an average arrival window of 30 to 45 minutes.
            </p>

            {/* Urgent Scenarios Triage List */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {urgentScenarios.map((scenario, index) => <div key={index} data-index={index} className="flex items-start gap-2.5">
                  <CircleAlert className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-foreground leading-snug">
                    {scenario}
                  </span>
                </div>)}
            </div>

            {/* Metrics Ribbon */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-xl bg-card border border-border mb-8">
              {responseMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col">
                  <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">
                    {metric.label}
                  </span>
                  <span className="text-xl sm:text-2xl font-bold text-primary my-0.5">
                    {metric.value}
                  </span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    {metric.detail}
                  </span>
                </div>)}
            </div>

            {/* Signature Dual CTA Cluster */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent hover:brightness-105 font-bold rounded-lg shadow-md h-14 px-8 text-base sm:text-lg transition-[filter,box-shadow]">
                <a href="tel:4708479076" className="flex items-center justify-center gap-3">
                  <Phone className="h-5 w-5 fill-current" />
                  <span>CALL (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-lg border-border text-foreground hover:bg-muted hover:text-foreground font-semibold h-14 px-6 transition-[background-color,border-color]">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Request Online Service</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Fleet Image & Visual Verification Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
              {/* Technician & Fleet Image */}
              <div className="aspect-[4/3] sm:aspect-[16/11] relative overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_plumbing_service_van.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_plumbing_service_van.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_plumbing_service_van.png-wvc-sizes" alt="Jonesboro emergency plumbing service vehicle prepared for fast local dispatch" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                
                {/* Image Overlay Tag */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-accent" />
                    <span className="text-sm font-semibold tracking-wide drop-shadow-sm">
                      Jonesboro, GA & Clayton County
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/80 backdrop-blur-xs text-xs font-medium text-primary-foreground">
                    <Truck className="h-3.5 w-3.5" />
                    Active Route
                  </span>
                </div>
              </div>

              {/* Supporting Mini Trust Card */}
              <Card className="rounded-lg border-t border-border shadow-md bg-card py-5">
                <CardContent className="px-5 py-0 flex items-start gap-3.5">
                  <div className="h-10 w-10 rounded-lg bg-secondary text-primary flex items-center justify-center shrink-0 mt-0.5">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">
                      24/7 Priority Emergency Staging
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Units are pre-staged near main Clayton County arteries (Hwy 19/41, Tara Blvd, and I-75) for fastest neighborhood routing.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Floating Guarantee Badge */}
            <div className="hidden sm:flex absolute -bottom-5 -left-5 bg-primary text-primary-foreground p-3.5 rounded-xl shadow-xl border border-primary/20 items-center gap-3 max-w-[260px]">
              <div className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0 font-bold">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-bold leading-tight">Guaranteed Response</span>
                <span className="text-[11px] text-primary-foreground/80 leading-tight mt-0.5">
                  Direct phone connection with on-duty master plumbers
                </span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}